import { useRbacEnabled } from '@/hooks/useRbacEnabled';
import { useWhoami } from '@/hooks/useWhoami';
interface Entitlements {
  canRead: boolean;
  canWriteAssets: boolean;
  canWriteJobs: boolean;
  canWriteFiles: boolean;
  canConverse: boolean;
  canManageAccounts: boolean;
  canManageSettings: boolean;
  canManageIntegrations: boolean;
  isPraetorian: boolean;
}

/**
 * Returns a boolean for each entitlement the current user may hold.
 *
 * When the Statsig `enable_rbac` gate is off, every property is `true`
 * (transparent / no enforcement).
 *
 * @example
 * ```tsx
 * const { canWriteAssets, canManageSettings } = useEntitlements();
 *
 * if (canWriteAssets) {
 *   deleteSelectedItems();
 * }
 * ```
 */
export function useEntitlements(): Entitlements {
  const rbacEnabled = useRbacEnabled();
  const { hasEntitlement } = useWhoami();

  if (!rbacEnabled) {
    return {
      canRead: true,
      canWriteAssets: true,
      canWriteJobs: true,
      canWriteFiles: true,
      canConverse: true,
      canManageAccounts: true,
      canManageSettings: true,
      canManageIntegrations: true,
      isPraetorian: true,
    };
  }

  return {
    canRead: hasEntitlement('read'),
    canWriteAssets: hasEntitlement('write_assets'),
    canWriteJobs: hasEntitlement('write_jobs'),
    canWriteFiles: hasEntitlement('write_files'),
    canConverse: hasEntitlement('conversation_ai'),
    canManageAccounts: hasEntitlement('manage_accounts'),
    canManageSettings: hasEntitlement('manage_settings'),
    canManageIntegrations: hasEntitlement('manage_integrations'),
    isPraetorian: hasEntitlement('praetorian'),
  };
}
